import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Sparkles, Mail, User, ThumbsUp, ThumbsDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Katie's photo for the chat bubble
const KATIE_AVATAR = "/images/katie/katie-thumbnail.jpg";

// Proactive messages - different for each page
const PROACTIVE_MESSAGES = {
  '/elevate': {
    greeting: "👋 Ready to scale your clinic?",
    message: "Elevate is designed for clinic owners ready to grow. Got questions about what's involved?",
    cta: "Ask me anything!"
  },
  '/ascend': {
    greeting: "👋 Considering Ascend?",
    message: "This is our highest-level programme for established clinic owners. Let me help you decide if it's right for you.",
    cta: "Let's explore!"
  },
  '/start-here': {
    greeting: "👋 Welcome to Thrive!",
    message: "Not sure where to start? I can help you find the right resources and programme for your goals.",
    cta: "Guide me!"
  },
  'default': {
    greeting: "👋 Hey there!",
    message: "Got questions about growing your clinic? I'm here to help!",
    cta: "Chat with me!"
  }
};

// Quick action pills for common questions
const quickActions = [
  { label: "Which programme is right for me?", icon: "🎯" },
  { label: "How much time do I need?", icon: "⏰" },
  { label: "Tell me about Elevate", icon: "📈" },
  { label: "What makes Thrive different?", icon: "✨" },
  { label: "I want to scale my clinic", icon: "🚀" },
];

const GrowthBuddy = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [leadEmail, setLeadEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [isCapturingLead, setIsCapturingLead] = useState(false);
  const [showProactivePopup, setShowProactivePopup] = useState(false);
  const [proactiveDismissed, setProactiveDismissed] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Get proactive message for current page
  const getProactiveMessage = () => {
    const path = location.pathname;
    for (const [key, value] of Object.entries(PROACTIVE_MESSAGES)) {
      if (key !== 'default' && path.startsWith(key)) {
        return value;
      }
    }
    return PROACTIVE_MESSAGES.default;
  };

  // Proactive trigger - show popup after 30 seconds on programme pages
  useEffect(() => {
    const proactivePages = Object.keys(PROACTIVE_MESSAGES).filter(k => k !== 'default');
    const isProactivePage = proactivePages.some(page => location.pathname.startsWith(page));
    const hasSeenProactive = localStorage.getItem('growthBuddyProactiveSeen');
    
    if (isProactivePage && !hasSeenProactive && !isOpen && !proactiveDismissed) {
      const timer = setTimeout(() => {
        setShowProactivePopup(true);
      }, 30000); // 30 seconds
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isOpen, proactiveDismissed]);

  // Show rating prompt after 5+ messages
  useEffect(() => {
    if (messages.length >= 6 && !hasRated && !showRating) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg?.role === 'assistant') {
        setShowRating(true);
      }
    }
  }, [messages, hasRated, showRating]);

  const handleProactiveClick = () => {
    setShowProactivePopup(false);
    setIsOpen(true);
    localStorage.setItem('growthBuddyProactiveSeen', 'true');
  };

  const dismissProactive = () => {
    setShowProactivePopup(false);
    setProactiveDismissed(true);
    localStorage.setItem('growthBuddyProactiveSeen', 'true');
  };

  const submitRating = async (rating) => {
    try {
      await fetch(`${API_URL}/api/chatbot/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          rating: rating,
          message_count: messages.length
        })
      });
    } catch (e) {
      console.error('Rating error:', e);
    }
    setHasRated(true);
    setShowRating(false);
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current && !showLeadCapture) {
      inputRef.current.focus();
    }
  }, [isOpen, showLeadCapture]);

  // Load session from localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem('growthBuddySession');
    const savedLead = localStorage.getItem('growthBuddyLead');
    if (savedSession) {
      setSessionId(savedSession);
      loadHistory(savedSession);
    }
    if (savedLead) {
      setLeadCaptured(true);
    }
  }, []);

  // Ask upfront who we're talking to - capture name and email before any chat
  useEffect(() => {
    if (isOpen && !leadCaptured) {
      setShowLeadCapture(true);
    }
  }, [isOpen, leadCaptured]);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const captureLead = async () => {
    if (!leadEmail.trim() || !leadName.trim()) return;
    if (!EMAIL_RE.test(leadEmail.trim())) {
      setEmailError("That doesn't look like a full email address - please double-check it");
      return;
    }
    setEmailError('');
    
    setIsCapturingLead(true);
    try {
      const response = await fetch(`${API_URL}/api/chatbot/capture-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: leadEmail,
          name: leadName,
          session_id: sessionId,
          newsletter_opt_in: newsletterOptIn
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.session_id && !sessionId) {
          setSessionId(data.session_id);
          localStorage.setItem('growthBuddySession', data.session_id);
        }
        localStorage.setItem('growthBuddyLead', leadEmail);
        setLeadCaptured(true);
        setShowLeadCapture(false);
        
        // Add a thank you message
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Thanks, ${leadName}! Great to meet you. Now, how can I help you today? And worth knowing: Katie personally looks at two clinics a week and gives them their next three moves - it's free. Apply for Katie's Plan for Your Clinic: https://thrive-businesscoaching.com/katies-plan`
        }]);
      } else if (response.status === 400) {
        setEmailError("That doesn't look like a full email address - please double-check it");
      }
    } catch (error) {
      console.error('Lead capture error:', error);
    } finally {
      setIsCapturingLead(false);
    }
  };

  const loadHistory = async (sid) => {
    try {
      const response = await fetch(`${API_URL}/api/chatbot/history/${sid}`);
      if (response.ok) {
        const data = await response.json();
        if (data.history && data.history.length > 0) {
          setMessages(data.history.map(msg => ({
            role: msg.role,
            content: msg.content
          })));
          setShowWelcome(false);
        }
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;
    if (!leadCaptured) {
      setShowLeadCapture(true);
      return;
    }

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setShowWelcome(false);

    // Add loading message
    setMessages(prev => [...prev, { role: 'assistant', content: '', loading: true }]);

    try {
      const response = await fetch(`${API_URL}/api/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: messageText,
          session_id: sessionId 
        })
      });

      if (!response.ok) throw new Error('Failed to send message');

      const data = await response.json();
      
      // Update with actual response
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          role: 'assistant',
          content: data.content
        };
        return newMessages;
      });

      // Save session ID
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('growthBuddySession', data.session_id);
      }

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = { 
          role: 'assistant', 
          content: "Oops! I had a little hiccup there. Could you try again? Or go straight to the team: apply for Katie's Plan for Your Clinic and Katie will personally look at your clinic: https://thrive-businesscoaching.com/katies-plan" 
        };
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickAction = (action) => {
    sendMessage(action.label);
  };

  const startNewChat = () => {
    setMessages([]);
    setShowWelcome(true);
    setSessionId(null);
    localStorage.removeItem('growthBuddySession');
  };

  // Convert URLs in text to clickable links
  const formatMessage = (content) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = content.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a 
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#E91E8C] hover:underline font-medium"
          >
            {part.includes('discovery-call') || part.includes('katies-plan') ? "📞 Apply for Katie's Plan" : 
             part.includes('/listen') ? '🎧 Listen Now' :
             part.includes('/watch') ? '📺 Watch Now' :
             part.includes('/learn') ? '📚 Read More' :
             part.includes('/elevate') ? '📈 Elevate Programme' :
             part.includes('/ascend') ? '🚀 Ascend Programme' :
             part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <>
      {/* Proactive Popup - Page-specific messages */}
      {showProactivePopup && !isOpen && (
        <div className="fixed bottom-24 right-6 z-50 animate-bounce-in">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 max-w-[300px] relative">
            <button 
              onClick={dismissProactive}
              className="absolute -top-2 -right-2 w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
              <img 
                src={KATIE_AVATAR} 
                alt="Katie Bell"
                className="w-12 h-12 rounded-full object-cover border-2 border-[#E91E8C]"
              />
              <div>
                <p className="text-sm text-gray-800 font-semibold mb-1">
                  {getProactiveMessage().greeting}
                </p>
                <p className="text-xs text-gray-600 mb-2">
                  {getProactiveMessage().message}
                </p>
                <button
                  onClick={handleProactiveClick}
                  className="text-sm bg-[#E91E8C] hover:bg-[#d11a7d] text-white px-4 py-2 rounded-full font-medium transition-colors"
                >
                  {getProactiveMessage().cta}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Toggle Button - Katie's Photo */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center overflow-hidden ${
          isOpen ? 'bg-gray-800' : 'hover:scale-110 border-4 border-[#E91E8C]'
        }`}
        data-testid="growth-buddy-toggle"
        aria-label={isOpen ? 'Close chat' : 'Open Growth Buddy chat'}
      >
        {isOpen ? (
          <X className="w-7 h-7 text-white" />
        ) : (
          <img 
            src={KATIE_AVATAR} 
            alt="Chat with Growth Buddy"
            className="w-full h-full object-cover"
          />
        )}
        
        {/* Notification dot */}
        {!isOpen && messages.length === 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#00B4D8] rounded-full flex items-center justify-center">
            <Sparkles className="w-3 h-3 text-white" />
          </span>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col"
          style={{ height: 'min(600px, calc(100vh - 150px))' }}
          data-testid="growth-buddy-chat"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#E91E8C] to-[#d11a7d] p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30">
                  <img 
                    src={KATIE_AVATAR} 
                    alt="Growth Buddy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Growth Buddy</h3>
                  <p className="text-xs text-pink-100">Your Thrive Assistant</p>
                </div>
              </div>
              <button 
                onClick={startNewChat}
                className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full transition-colors"
              >
                New Chat
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {/* Welcome Message */}
            {showWelcome && messages.length === 0 && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl rounded-tl-sm p-4 shadow-sm border border-gray-100">
                  <p className="text-gray-800 mb-2">
                    👋 Hey there! I&apos;m <strong>Growth Buddy</strong>, your friendly guide to all things Thrive Business Coaching.
                  </p>
                  <p className="text-gray-600 text-sm">
                    I can help you figure out which programme is right for you, recommend podcast episodes, and answer your questions about growing your clinic business.
                  </p>
                </div>
                
                {/* Quick Actions */}
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 font-medium px-1">Quick questions:</p>
                  <div className="flex flex-wrap gap-2">
                    {quickActions.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuickAction(action)}
                        disabled={!leadCaptured}
                        className="bg-white hover:bg-[#E91E8C] hover:text-white text-gray-700 text-sm px-3 py-2 rounded-full border border-gray-200 hover:border-[#E91E8C] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-700"
                        data-testid={`quick-action-${index}`}
                      >
                        {action.icon} {action.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Chat Messages */}
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl p-3 ${
                    message.role === 'user'
                      ? 'bg-[#E91E8C] text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-tl-sm shadow-sm border border-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">
                    {message.role === 'assistant' ? formatMessage(message.content) : message.content}
                  </p>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && messages[messages.length - 1]?.role === 'user' && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-tl-sm p-3 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Growth Buddy is typing...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Rating prompt */}
            {showRating && !hasRated && (
              <div className="flex justify-center my-2">
                <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
                  <p className="text-xs text-gray-600 mb-2">Was this conversation helpful?</p>
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => submitRating('positive')}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-full text-xs font-medium transition-colors"
                    >
                      <ThumbsUp className="w-3 h-3" /> Yes!
                    </button>
                    <button
                      onClick={() => submitRating('negative')}
                      className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full text-xs font-medium transition-colors"
                    >
                      <ThumbsDown className="w-3 h-3" /> Not really
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Thank you for rating */}
            {hasRated && (
              <div className="flex justify-center my-2">
                <div className="bg-green-50 rounded-xl px-4 py-2 text-center">
                  <p className="text-xs text-green-700">Thanks for your feedback! 💚</p>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions (shown after conversation starts) */}
          {messages.length > 0 && !isLoading && (
            <div className="px-4 py-2 bg-white border-t border-gray-100">
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {quickActions.slice(0, 3).map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickAction(action)}
                    className="flex-shrink-0 bg-gray-100 hover:bg-[#E91E8C] hover:text-white text-gray-600 text-xs px-3 py-1.5 rounded-full transition-all whitespace-nowrap"
                  >
                    {action.icon} {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Lead Capture Form - Required name and email */}
          {showLeadCapture && (
            <div className="px-4 py-3 bg-gradient-to-r from-[#E91E8C]/10 to-[#00B4D8]/10 border-t border-[#E91E8C]/20">
              <div className="text-center mb-3">
                <p className="text-sm font-semibold text-gray-800" data-testid="lead-capture-heading">👋 First things first - who am I chatting with?</p>
                <p className="text-xs text-gray-600">Pop your name and email in below so we can get started!</p>
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={leadName}
                    onChange={(e) => setLeadName(e.target.value)}
                    placeholder="Your name"
                    data-testid="chat-lead-name-input"
                    className="w-full pl-9 pr-3 py-2 bg-white rounded-lg text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/50"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={leadEmail}
                      onChange={(e) => { setLeadEmail(e.target.value); if (emailError) setEmailError(''); }}
                      placeholder="Your email"
                      data-testid="chat-lead-email-input"
                      className="w-full pl-9 pr-3 py-2 bg-white rounded-lg text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/50"
                      required
                    />
                  </div>
                  <button
                    onClick={captureLead}
                    data-testid="chat-lead-continue-btn"
                    disabled={!leadEmail.trim() || !leadName.trim() || isCapturingLead}
                    className="px-4 py-2 bg-[#E91E8C] hover:bg-[#d11a7d] disabled:bg-gray-300 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {isCapturingLead ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
                  </button>
                </div>
                {emailError && (
                  <p className="text-xs text-red-500 mt-1" data-testid="chat-email-error">{emailError}</p>
                )}
                <label className="flex items-start gap-2 mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newsletterOptIn}
                    onChange={(e) => setNewsletterOptIn(e.target.checked)}
                    data-testid="chat-newsletter-consent"
                    className="mt-0.5 accent-[#E91E8C]"
                  />
                  <span className="text-xs text-gray-600">Email me Katie's weekly clinic growth tips too. Unsubscribe anytime.</span>
                </label>
              </div>
            </div>
          )}

          {/* Input Area */}
          <form onSubmit={handleSubmit} className="p-4 bg-white border-t border-gray-100">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={leadCaptured ? "Ask me anything..." : "Enter your details above to start chatting..."}
                className="flex-1 px-4 py-3 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/50 transition-all disabled:cursor-not-allowed"
                disabled={isLoading || !leadCaptured}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading || !leadCaptured}
                className="w-12 h-12 bg-[#E91E8C] hover:bg-[#d11a7d] disabled:bg-gray-300 text-white rounded-full flex items-center justify-center transition-colors"
                data-testid="chat-send"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default GrowthBuddy;
